import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Receipt, Plane, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

export default function EmployeeInboxTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/me/inbox');
        if (!cancelled) setData(r.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-8 text-center text-muted-foreground">No inbox data</div>;

  const { counts = {}, pending_claims = [], pending_leaves = [], decided_claims_recent = [], decided_leaves_recent = [] } = data;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="employee-inbox-summary">
        <Card><CardContent className="p-3 text-center">
          <div className="text-2xl font-bold text-amber-600">{counts.pending_claims || 0}</div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Claims pending</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <div className="text-2xl font-bold text-blue-600">{counts.pending_leaves || 0}</div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Leaves pending</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <div className="text-2xl font-bold text-emerald-600">{counts.pending_acknowledgments || 0}</div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Payslips to ack</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-sm flex items-center gap-2"><Receipt size={14} /> My pending expense claims</CardTitle></CardHeader>
        <CardContent className="p-0">
          {pending_claims.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">No pending claims.</p>
          ) : (
            <ul className="divide-y divide-border">
              {pending_claims.map(c => (
                <li key={c.id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`my-claim-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium capitalize">{c.category} <span className="text-muted-foreground font-normal">· {(c.created_at || '').slice(0, 10)}</span></div>
                    <div className="text-xs text-muted-foreground truncate max-w-[400px]">{c.description}</div>
                  </div>
                  <div className="font-mono font-bold ml-2 shrink-0">{c.currency || 'SAR'} {Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-sm flex items-center gap-2"><Plane size={14} /> My pending leaves</CardTitle></CardHeader>
        <CardContent className="p-0">
          {pending_leaves.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">No pending leave requests.</p>
          ) : (
            <ul className="divide-y divide-border">
              {pending_leaves.map(l => (
                <li key={l.id} className="px-4 py-2 flex items-center justify-between text-sm" data-testid={`my-leave-${l.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium capitalize">{l.leave_type}</div>
                    <div className="text-xs text-muted-foreground font-mono">{(l.start_date || '').slice(0, 10)} → {(l.end_date || '').slice(0, 10)}</div>
                  </div>
                  <div className="font-mono ml-2 shrink-0">{l.days_count}d</div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {(decided_claims_recent.length + decided_leaves_recent.length > 0) && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="font-outfit text-sm flex items-center gap-2"><CheckCircle2 size={14} /> Recent decisions</CardTitle></CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {decided_claims_recent.map(c => (
                <li key={c.id} className="px-4 py-2 flex items-center justify-between text-xs">
                  <div>
                    <span className={`px-1.5 py-0.5 rounded font-medium ${c.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{c.status}</span>
                    <span className="ml-2 capitalize">Claim · {c.category}</span>
                  </div>
                  <div className="font-mono">{c.currency || 'SAR'} {Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </li>
              ))}
              {decided_leaves_recent.map(l => (
                <li key={l.id} className="px-4 py-2 flex items-center justify-between text-xs">
                  <div>
                    <span className={`px-1.5 py-0.5 rounded font-medium ${l.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{l.status}</span>
                    <span className="ml-2 capitalize">Leave · {l.leave_type}</span>
                  </div>
                  <div className="font-mono">{l.days_count}d</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
